import { useEffect } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { Anchor, Stack, Text, Title } from "@mantine/core";
import { CollectionTile } from "./CollectionTile";
import { useStores } from "@/hooks/useStores";
import { Urls } from "@/core/urls";

const FEATURED_COLLECTIONS = 2;
const FEATURED_SUBJECTS = 4;

/** Picks `count` items starting at an offset that changes daily, so the shelf rotates. */
const rotate = <T,>(items: T[], count: number): T[] => {
    if (items.length <= count) {
        return items;
    }
    const day = Math.floor(Date.now() / 86_400_000);
    return Array.from({ length: count }, (_, i) => items[(day + i) % items.length]);
};

/** Home sidebar shelf that leads into the collections and focus areas. */
export const FeaturedCollections = observer(() => {
    const { collectionStore, subjectStore } = useStores();

    useEffect(() => {
        collectionStore.loadList().catch(console.error);
        subjectStore.loadList().catch(console.error);
    }, [collectionStore, subjectStore]);

    const collections = rotate(collectionStore.collections, FEATURED_COLLECTIONS);
    const subjects = rotate(subjectStore.subjects, FEATURED_SUBJECTS);

    if (collections.length === 0 && subjects.length === 0) {
        return null;
    }

    return (
        <Stack gap="xl" mb="xl" mt="xl">
            {collections.length > 0 && (
                <section aria-labelledby="home-collections">
                    <Title id="home-collections" mb="md" order={4}>
                        Poddsamlingar
                    </Title>
                    <Stack gap="md">
                        {collections.map((collection: Collection) => (
                            <CollectionTile collection={collection} key={collection.id} />
                        ))}
                    </Stack>
                    <Anchor component={Link} display="block" mt="md" size="sm" to={Urls.collections()}>
                        Alla samlingar →
                    </Anchor>
                </section>
            )}

            {subjects.length > 0 && (
                <section aria-labelledby="home-subjects">
                    <Title id="home-subjects" mb="xs" order={4}>
                        Fokus
                    </Title>
                    <Text c="dimmed" mb="md" size="sm">
                        Avsnitt från flera poddar om ett och samma ämne.
                    </Text>
                    <Stack gap="sm">
                        {subjects.map((subject: Subject) => (
                            <Anchor component={Link} fw={600} key={subject.slug} to={Urls.focusArea(subject.slug)}>
                                {subject.name}
                            </Anchor>
                        ))}
                    </Stack>
                    <Anchor component={Link} display="block" mt="md" size="sm" to={Urls.focusAreas()}>
                        Alla fokusområden →
                    </Anchor>
                </section>
            )}
        </Stack>
    );
});
